class IndexedDB {
  constructor () {
    this.dbName = 'bilyoner';
    this.storeName = 'store';
  }

  // No explicit version: opens at whatever version already exists (or creates at 1).
  // Avoids ever hardcoding/drifting a version number across page loads.
  _open = () => new Promise((resolve, reject) => {
    const request = indexedDB.open(this.dbName);

    request.onupgradeneeded = event => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(this.storeName)) {
        db.createObjectStore(this.storeName);
      }
    };

    request.onsuccess = () => {
      const db = request.result;

      if (db.objectStoreNames.contains(this.storeName)) {
        resolve(db);
        return;
      }

      const nextVersion = db.version + 1;
      db.close();

      const upgradeRequest = indexedDB.open(this.dbName, nextVersion);
      upgradeRequest.onupgradeneeded = event => {
        const upgraded = event.target.result;
        if (!upgraded.objectStoreNames.contains(this.storeName)) {
          upgraded.createObjectStore(this.storeName);
        }
      };
      upgradeRequest.onsuccess = () => resolve(upgradeRequest.result);
      upgradeRequest.onerror = () => reject(upgradeRequest.error);
      upgradeRequest.onblocked = () => reject(new Error('[IndexedDB] upgrade blocked by another open connection'));
    };

    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('[IndexedDB] open blocked by another connection'));
  });

  _withStore = async (mode, execute) => {
    const db = await this._open();

    try {
      if (!db.objectStoreNames.contains(this.storeName)) {
        return null;
      }

      const tx = db.transaction([this.storeName], mode);
      const store = tx.objectStore(this.storeName);
      const txDone = new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });

      const result = await execute(store);
      await txDone;
      return result;
    } finally {
      db.close();
    }
  };

  initialize () {
    return this._open()
      .then(db => db.close())
      .catch(err => {
        console.error('[IndexedDB] initialize failed:', err);
      });
  }

  get (key) {
    return this._withStore('readonly', store => new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    })).catch(err => {
      console.error('[IndexedDB] get failed:', err);
    });
  }

  set (key, value) {
    return this._withStore('readwrite', store => {
      store.put(value, key);
    });
  }

  remove (key) {
    return this._withStore('readwrite', store => {
      store.delete(key);
    });
  }
}

const db = new IndexedDB();
export default db;
